import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import { CANVAS_GUIDE_CURRENT_STEP } from '../../../Rutab/RutabConstants';
import { translate } from "../../../Services/translation";
const GuidedTour = ({ steps, isOpen, onClose, onComplete, }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [position, setPosition] = useState({
        top: 0,
        left: 0,
        width: 0,
        height: 0,
    });
    const [isVisible, setIsVisible] = useState(false);
    const highlightRef = useRef(null);
    const tooltipRef = useRef(null);
    const clickHandlersRef = useRef([]);
    const currentStepRef = useRef(0);
    const targetElementRef = useRef(null);
    const dispatch = useDispatch();
    const findNextMainStep = (currentIndex) => {
        for (let i = currentIndex + 1; i < steps.length; i++) {
            if (!steps[i].needsToBeOpened) {
                return i;
            }
        }
        return steps.length - 1;
    };
    const findPreviousMainStep = (currentIndex) => {
        for (let i = currentIndex - 1; i >= 0; i--) {
            if (!steps[i].needsToBeOpened) {
                return i;
            }
        }
        return 0;
    };
    const findTargetElement = (stepIndex) => {
        if (!steps[stepIndex])
            return null;
        const selector = steps[stepIndex].target;
        const element = document.querySelector(selector);
        if (!element) {
            console.warn(`Target element not found: ${selector}`);
            return null;
        }
        return element;
    };
    const calculatePosition = useCallback(() => {
        const step = steps[currentStep];
        if (!step)
            return;
        const element = findTargetElement(currentStep);
        if (!element)
            return;
        targetElementRef.current = element;
        const rect = element.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
        setPosition({
            top: rect.top + scrollTop,
            left: rect.left + scrollLeft,
            width: rect.width,
            height: rect.height,
        });
        document.querySelectorAll('.tour-highlight-pulse').forEach((el) => {
            el.classList.remove('tour-highlight-pulse');
        });
        element.classList.add('tour-highlight-pulse');
        cleanupClickHandlers();
        if (step.waitForClick) {
            const stepIndex = currentStep;
            const handleClick = (e) => {
                element.classList.remove('tour-highlight-pulse');
                setCurrentStep(stepIndex + 1);
            };
            element.addEventListener('click', handleClick, { once: true });
            clickHandlersRef.current.push({ element, handler: handleClick });
        }
        setTimeout(() => {
            updateTooltipPosition();
        }, 50);
    }, [currentStep, steps]);
    const updateTooltipPosition = () => {
        var _a;
        if (!tooltipRef.current || !steps[currentStep])
            return;
        const rect = (_a = targetElementRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
        if (!rect)
            return;
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
        let tooltipTop = 0;
        let tooltipLeft = 0;
        switch (steps[currentStep].position) {
            case 'top':
                tooltipTop = rect.top + scrollTop - tooltipRect.height - 10;
                tooltipLeft =
                    rect.left + scrollLeft + rect.width / 2 - tooltipRect.width / 2;
                break;
            case 'right':
                tooltipTop =
                    rect.top + scrollTop + rect.height / 2 - tooltipRect.height / 2;
                tooltipLeft = rect.left + scrollLeft + rect.width + 10;
                break;
            case 'bottom':
                tooltipTop = rect.top + scrollTop + rect.height + 10;
                tooltipLeft =
                    rect.left + scrollLeft + rect.width / 2 - tooltipRect.width / 2;
                break;
            case 'left':
                tooltipTop =
                    rect.top + scrollTop + rect.height / 2 - tooltipRect.height / 2;
                tooltipLeft = rect.left + scrollLeft - tooltipRect.width - 10;
                break;
        }
        if (tooltipLeft < scrollLeft + 20) {
            tooltipLeft = scrollLeft + 20;
        }
        else if (tooltipLeft + tooltipRect.width >
            scrollLeft + viewportWidth - 20) {
            tooltipLeft = scrollLeft + viewportWidth - tooltipRect.width - 20;
        }
        if (tooltipTop < scrollTop + 20) {
            tooltipTop = scrollTop + 20;
        }
        else if (tooltipTop + tooltipRect.height >
            scrollTop + viewportHeight - 20) {
            tooltipTop = scrollTop + viewportHeight - tooltipRect.height - 20;
        }
        tooltipRef.current.style.top = `${tooltipTop}px`;
        tooltipRef.current.style.left = `${tooltipLeft}px`;
    };
    const cleanupClickHandlers = () => {
        clickHandlersRef.current.forEach(({ element, handler }) => {
            element.removeEventListener('click', handler);
        });
        clickHandlersRef.current = [];
    };
    const goToNextStep = () => {
        var _a, _b;
        const currentElement = document.querySelector((_a = steps[currentStep]) === null || _a === void 0 ? void 0 : _a.target);
        if (currentElement) {
            currentElement.classList.remove('tour-highlight-pulse');
        }
        cleanupClickHandlers();
        if ((_b = steps[currentStep]) === null || _b === void 0 ? void 0 : _b.action) {
            steps[currentStep].action();
        }
        if (currentStep < steps.length - 1) {
            setCurrentStep((prev) => prev + 1);
        }
        else {
            handleComplete();
        }
    };
    const goToPreviousStep = () => {
        var _a;
        const currentElement = document.querySelector((_a = steps[currentStep]) === null || _a === void 0 ? void 0 : _a.target);
        if (currentElement) {
            currentElement.classList.remove('tour-highlight-pulse');
        }
        cleanupClickHandlers();
        if (currentStep > 0) {
            const prevMainStep = findPreviousMainStep(currentStep);
            setCurrentStep(prevMainStep);
        }
    };
    const handleClose = () => {
        document.querySelectorAll('.tour-highlight-pulse').forEach((el) => {
            el.classList.remove('tour-highlight-pulse');
        });
        cleanupClickHandlers();
        setIsVisible(false);
        onClose();
    };
    const handleComplete = () => {
        document.querySelectorAll('.tour-highlight-pulse').forEach((el) => {
            el.classList.remove('tour-highlight-pulse');
        });
        cleanupClickHandlers();
        setIsVisible(false);
        onComplete();
    };
    const handleSkipToNextMainStep = () => {
        var _a;
        const currentElement = document.querySelector((_a = steps[currentStep]) === null || _a === void 0 ? void 0 : _a.target);
        if (currentElement) {
            currentElement.classList.remove('tour-highlight-pulse');
        }
        cleanupClickHandlers();
        const nextMainStep = findNextMainStep(currentStep);
        if (nextMainStep >= steps.length - 1) {
            handleComplete();
        }
        else {
            setCurrentStep(nextMainStep);
        }
    };
    useEffect(() => {
        if (isOpen) {
            setCurrentStep(0);
            currentStepRef.current = 0;
            setIsVisible(false);
            document.querySelectorAll('.tour-highlight-pulse').forEach((el) => {
                el.classList.remove('tour-highlight-pulse');
            });
            cleanupClickHandlers();
            setTimeout(() => {
                setIsVisible(true);
                const firstElement = findTargetElement(0);
                if (firstElement) {
                    targetElementRef.current = firstElement;
                    setTimeout(() => {
                        calculatePosition();
                    }, 100);
                }
            }, 100);
        }
        else {
            setIsVisible(false);
        }
    }, [isOpen]);
    useEffect(() => {
        currentStepRef.current = currentStep;
        if (isVisible && steps[currentStep]) {
            dispatch({
                type: CANVAS_GUIDE_CURRENT_STEP,
                payload: { decalGuide: steps[currentStep] },
            });
            const timer = setTimeout(() => {
                calculatePosition();
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [currentStep, steps, isVisible]);
    useEffect(() => {
        if (isVisible) {
            const handleResize = () => {
                calculatePosition();
            };
            window.addEventListener('resize', handleResize);
            return () => {
                window.removeEventListener('resize', handleResize);
            };
        }
    }, [isVisible]);
    useEffect(() => {
        return () => {
            document.querySelectorAll('.tour-highlight-pulse').forEach((el) => {
                el.classList.remove('tour-highlight-pulse');
            });
            cleanupClickHandlers();
        };
    }, []);
    useEffect(() => {
        currentStepRef.current = currentStep;
    }, [currentStep]);
    if (!isVisible || !steps[currentStep]) {
        return null;
    }
    const step = steps[currentStep];
    const hasPreviousStep = currentStep > 0;
    const isLastStep = currentStep === steps.length - 1;
    return createPortal(_jsxs(_Fragment, { children: [_jsx("div", { className: "guided-tour-overlay", onClick: handleClose }), _jsx("div", { ref: highlightRef, className: "guided-tour-highlight", style: {
                    top: `${position.top}px`,
                    left: `${position.left}px`,
                    width: `${position.width}px`,
                    height: `${position.height}px`,
                    boxShadow: '0 0 0 5000px rgba(0, 0, 0, 0.3)',
                } }), _jsxs("div", { ref: tooltipRef, className: "guided-tour-tooltip", "data-position": step.position, children: [_jsx("div", { className: "guided-tour-progress", style: { display: 'flex', gap: '6px' }, children: steps.map((_, i) => (_jsx("span", { className: `guided-tour-dot ${i === currentStep ? 'active' : ''}`, style: {
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: i === currentStep ? '#007bff' : '#ddd',
                            } }, i))) }), _jsx("h4", { children: translate(step.title) }), _jsx("p", { children: translate(step.content) }), _jsx("div", { style: { display: 'flex', gap: '8px', justifyContent: 'flex-end' }, className: "mb-1", children: !step.waitForClick && (_jsx("button", { className: "guided-tour-next", onClick: goToNextStep, style: {
                                backgroundColor: '#007bff',
                                color: 'white',
                                border: 'none',
                                padding: '6px 12px',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontWeight: 500,
                            }, children: isLastStep ? translate('decal.guided-tour.button.exit') : translate('decal.guided-tour.button.next') })) }), _jsxs("div", { className: "guided-tour-buttons mt-1", style: {
                            display: 'flex',
                            gap: '8px',
                            justifyContent: 'space-between',
                            marginTop: '16px',
                        }, children: [_jsx("div", { children: hasPreviousStep && (_jsx("button", { className: "guided-tour-prev", onClick: goToPreviousStep, style: {
                                        background: 'none',
                                        border: '1px solid #007bff',
                                        color: '#007bff',
                                        padding: '6px 12px',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                    }, children: translate('decal.guided-tour.button.previous') })) }), _jsx("div", { children: _jsx("button", { className: "guided-tour-skip", onClick: handleSkipToNextMainStep, style: {
                                        background: 'none',
                                        border: 'none',
                                        color: '#666',
                                        padding: '6px 12px',
                                        cursor: 'pointer',
                                    }, children: isLastStep ? translate('decal.guided-tour.button.exit') : translate('decal.guided-tour.button.skiptonext') }) })] })] })] }), document.body);
};
export default GuidedTour;
